import { AppError } from '@app/errors/AppError'

import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import { PriceInputValue } from '@app/utils/PriceInputValue'

import type { ApiPlan } from '@app/packages/api/v2/types/entities'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { profileRegionIdResultSelector } from './profile_place'

const plansSlice = createReduxSlice<SliceState<ApiPlan[]>>('plans')

export const plansResultSelector = createSelector([plansSlice.selector], slice =>
  createLoadable(c => {
    return withCheckedSliceState(c, {
      sliceState: slice,
      slice: plansSlice,
      load: async dispatch => {
        const { getPlans } = await import('@app/packages/api/v2/actions/plans')
        const resp = await dispatch(callV2Api(getPlans()))
        return resp.data
      },
    })
  })
)

export const currentPlanResultSelector = createSelector([profileRegionIdResultSelector, plansResultSelector], (regionIdResult, plansResult) =>
  createLoadable(c => {
    const plans = c.unwrap(plansResult)
    const regionId = c.unwrap(regionIdResult)
    return plans.find(p => p.attributes.city_ids.includes(regionId)) ?? invariant(new AppError(`Plan for ${regionId} not found`))
  })
)

export const commissionPriceResultSelector = mapThunkSelector(currentPlanResultSelector, plan => {
  return PriceInputValue.formatted(plan.attributes.commission_fee, plan.attributes.subscription_options.at(0)!.currency)
})

export const subscriptionPriceResultSelector = mapThunkSelector(currentPlanResultSelector, plan => {
  const option = plan.attributes.subscription_options.find(o => o.period_months === 1)!
  return PriceInputValue.formatted(option.price_for_month, option.currency)
})
